import { swapSoftNavPage, fetchSoftNavPage, beginSoftNav, endSoftNav } from '@theme/section-swap';

if (!(/** @type {any} */ (window).__lengthSoftNavInit)) {
  /** @type {any} */ (window).__lengthSoftNavInit = true;

  const NAVIGATION_LINKS = 'a[data-length-navigate], a[data-gear-set-navigate]';
  const PREFETCH_TTL = 60_000;
  const PREFETCH_MAX = 6;

  /**
   * Pages warmed on hover/focus of a length or gear-set link, so the click
   * swaps instantly. Keyed by URL; a stale entry is refetched.
   * @type {Map<string, { promise: Promise<string>, timestamp: number }>}
   */
  const prefetched = new Map();

  /**
   * @param {string} url
   * @param {AbortSignal} [signal]
   * @returns {Promise<string>}
   */
  const fetchPage = (url, signal) => {
    const cached = prefetched.get(url);
    if (cached && Date.now() - cached.timestamp < PREFETCH_TTL) return cached.promise;

    const promise = fetchSoftNavPage(url, signal).catch((error) => {
      prefetched.delete(url);
      throw error;
    });
    prefetched.set(url, { promise, timestamp: Date.now() });
    while (prefetched.size > PREFETCH_MAX) {
      const oldest = prefetched.keys().next().value;
      if (oldest === undefined) break;
      prefetched.delete(oldest);
    }
    return promise;
  };

  /**
   * @param {string} url
   * @param {{ pushState: boolean }} options
   */
  const runSoftNav = async (url, { pushState }) => {
    // Shared with product-variations.js — starting a nav here aborts any
    // colourway swap in flight, and vice versa, so the two never race.
    const thisController = beginSoftNav();

    const mainSectionElement = document.querySelector('#shopify-section-main');
    mainSectionElement?.classList.add('is-loading');
    mainSectionElement?.setAttribute('aria-busy', 'true');

    const scrollPosition = window.scrollY;

    try {
      await swapSoftNavPage(url, { signal: thisController.signal, fetchPage });
      window.scrollTo(0, scrollPosition);
      if (pushState) {
        // The entry being left is usually the initial page load, whose state
        // carries no softNav flag; mark it so going back to it also refreshes.
        if (!history.state?.softNav) history.replaceState({ ...(history.state ?? {}), softNav: true }, '');
        history.pushState({ softNav: true }, '', url);
      }
    } catch (error) {
      if (/** @type {Error} */ (error).name !== 'AbortError') window.location.href = url;
    } finally {
      endSoftNav(thisController);
      mainSectionElement?.classList.remove('is-loading');
      mainSectionElement?.setAttribute('aria-busy', 'false');
    }
  };

  /** @param {Event} event */
  const prefetchLink = (event) => {
    // pointerenter also fires on the document itself when the pointer enters the page.
    if (!(event.target instanceof Element)) return;
    const navigationLink = event.target.closest(NAVIGATION_LINKS);
    if (!(navigationLink instanceof HTMLAnchorElement)) return;
    fetchPage(navigationLink.href).catch(() => {});
  };
  document.addEventListener('pointerenter', prefetchLink, { capture: true, passive: true });
  document.addEventListener('focusin', prefetchLink, { passive: true });

  document.addEventListener('click', (event) => {
    const navigationLink = /** @type {HTMLElement | null} */ (event.target)?.closest(NAVIGATION_LINKS);
    if (!navigationLink) return;
    event.preventDefault();
    event.stopPropagation();
    runSoftNav(/** @type {HTMLAnchorElement} */ (navigationLink).href, { pushState: true });
  });

  window.addEventListener('popstate', (event) => {
    if (!event.state?.softNav) return;
    // A standalone product-variations navigate-mode instance on this page already
    // owns popstate-driven section swaps (same main+recommendations refetch, plus
    // product-specific handling) — defer to it instead of double-fetching.
    if (/** @type {any} */ (window).__productVariationsPopstateOwner) return;
    runSoftNav(document.location.href, { pushState: false });
  });
}
